<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import type { IntegrationGrundfosModel } from './Model';
import { events } from '@/src/services/events';
import type { AddonRegistrationModel } from '@/src/components/addons/registration/Model';
import type { SectionModelType } from '@/src/typings/types/types';
import { useUtilityStore } from '@/src/store/utility';
import type { DataLayerData } from '@/src/utilities/Tracking';
import { useHead } from '@vueuse/head';

interface TrackForm {
  name: string;
  type: string;
  event: string;
  error: string;
}

interface dataLayer {
  pageName: string;
  rsid: string;
  platform: string;
  server: string;
  companyCode: string;
  siteCountry: string;
  hier1: string;
  channelTitle: string;
  language: string;
  form?: TrackForm;
  permissions?: undefined | string[];
}

export default defineComponent({
  name: 'IntegrationGrundfos',
  props: {
    model: {
      type: Object as PropType<IntegrationGrundfosModel>,
      required: true
    }
  },
  setup(props) {
    const utilityStore = useUtilityStore();
    const isDemo = (utilityStore.url ?? window.location.href).includes('/campaign/view/demo');

    let lastDataLayerEntry: DataLayerData = {
      pageName: '',
      rsid: '',
      platform: 'leadFamly',
      server: 'games.grundfos.com',
      companyCode: '',
      siteCountry: '',
      hier1: '',
      channelTitle: '',
      language: ''
    };

    if (isDemo) {
      useHead({
        script: [
          {
            src: 'https://assets.adobedtm.com/5e107551ef5d/73a515d008be/launch-d2b96faf88a9-staging.min.js',
            key: 'grundfos-staging-satellite-script'
          }
        ]
      });
    } else {
      useHead({
        script: [
          {
            src: 'https://assets.adobedtm.com/5e107551ef5d/73a515d008be/launch-61f55b6a98f0.min.js',
            key: 'grundfos-satellite-script'
          }
        ]
      });
    }

    let hasFormBeenSubmitted = false;
    let satelliteLoaded = false;

    const waitForSatellite = (): Promise<void> => {
      if (satelliteLoaded) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const checkSatellite = () => {
          if (typeof window._satellite !== 'undefined') {
            // Only override getVar if it hasn't been overridden yet
            const oldGetVar = window._satellite.getVar;
            window._satellite.getVar = (variableName: string) => {
              if (window.dataLayer && window.dataLayer.length > 0) {
                for (const dataLayerItem of window.dataLayer) {
                  if (Object.prototype.hasOwnProperty.call(dataLayerItem, variableName)) {
                    return dataLayerItem[String(variableName)];
                  }
                }
              }

              return oldGetVar(variableName);
            };

            satelliteLoaded = true;

            resolve();
          } else {
            setTimeout(checkSatellite, 250);
          }
        };

        checkSatellite();
      });
    };

    waitForSatellite().then(() => {
      if (!window._satellite) return;
      window._satellite.pageBottom();
    });

    events.on('flowPageChange', async (_fromPage, toPage) => {
      if (toPage && props.model.state.tracking?.adobe) {
        await waitForSatellite();

        if (toPage.getAddons<AddonRegistrationModel>('registration').length > 0) {
          trackFormEvents('viewed', '', toPage.getSection());
        }

        const trackingConfig = props.model.state.tracking.adobe;
        const pageName = toPage.state.title ? toPage.state.title : 'step' + (toPage.index + 1);
        if (typeof window.dataLayer === 'undefined') {
          window.dataLayer = [];
        }

        const newDataLayer: dataLayer = {
          pageName: `${trackingConfig.companyCode}:leadFamly:${pageName.toLowerCase()}`,
          rsid: trackingConfig.rsid,
          platform: 'leadFamly',
          server: 'games.grundfos.com',
          companyCode: trackingConfig.companyCode,
          siteCountry: trackingConfig.siteCountry,
          hier1: `${trackingConfig.companyCode}/leadFamly/${trackingConfig.language}`,
          channelTitle: pageName,
          language: trackingConfig.language
        };

        const updatedDataLayer = { ...lastDataLayerEntry, ...newDataLayer };

        window.dataLayer = window.dataLayer.filter((item) => item.server !== 'games.grundfos.com');

        window.dataLayer.push(updatedDataLayer);

        lastDataLayerEntry = updatedDataLayer;

        window._satellite?.track('page view');

        // eslint-disable-next-line no-console
        console.log('Adobe Analytics debugging: Tracked page event: page view', updatedDataLayer);
      }
    });

    events.on('formSubmitted', (pageModel: SectionModelType) => {
      trackFormEvents('sent', '', pageModel);
    });

    events.on('formInvalidFields', (pageModel: SectionModelType) => {
      trackFormEvents('error', 'One or more fields have not been filled out correctly', pageModel);
    });

    events.on('formRegistrationLimit', (pageModel: SectionModelType) => {
      trackFormEvents('error', 'Registration limit reached', pageModel);
    });

    const trackFormEvents = async (event: string, errorMessage = '', pageModel: SectionModelType) => {
      if (hasFormBeenSubmitted && event === 'sent') return;

      if (typeof window.dataLayer === 'undefined') {
        window.dataLayer = [];
      }

      const pageName = pageModel.state.title ? pageModel.state.title : 'step' + (pageModel.index + 1);

      const trackData: TrackForm = {
        name: pageName,
        type: 'register',
        event,
        error: errorMessage
      };

      const trackingConfig = props.model.state.tracking?.adobe;
      if (!trackingConfig) return;

      const newDataLayer: DataLayerData = {
        pageName: `${trackingConfig.companyCode}:leadFamly:${pageName.toLowerCase()}`,
        rsid: trackingConfig.rsid,
        platform: 'leadFamly',
        server: 'games.grundfos.com',
        companyCode: trackingConfig.companyCode,
        siteCountry: trackingConfig.siteCountry,
        hier1: `${trackingConfig.companyCode}/leadFamly/${trackingConfig.language}`,
        channelTitle: pageName,
        language: trackingConfig.language
      };

      const updatedDataLayer = { ...lastDataLayerEntry, ...newDataLayer };

      if (event === 'sent' && pageModel) {
        hasFormBeenSubmitted = true;
        const permissionsFlatArray: string[] = [];

        pageModel.getAddons<AddonRegistrationModel>('registration').forEach((registrationModel) => {
          registrationModel.state.fields?.forEach((field) => {
            if (field.state.inputHtmlType === 'checkbox' && field.state?.value) {
              const fieldId = field.state.id;
              const permissionsObject = props.model.state.tracking?.adobe?.permissions;

              if (permissionsObject && Object.prototype.hasOwnProperty.call(permissionsObject, fieldId)) {
                permissionsFlatArray.push(...permissionsObject[Number(fieldId)].map(String));
              }
            }
          });
        });

        if (permissionsFlatArray) {
          updatedDataLayer.permissions = permissionsFlatArray;

          // eslint-disable-next-line no-console
          console.log('Adobe Analytics debugging: Tracked permission event ', permissionsFlatArray, updatedDataLayer);
        }
      }

      updatedDataLayer.form = trackData;

      lastDataLayerEntry = updatedDataLayer;

      await waitForSatellite();
      window.dataLayer = window.dataLayer.filter((item) => item.server !== 'games.grundfos.com');
      window.dataLayer.push(updatedDataLayer);
      window._satellite?.track('form event');

      // eslint-disable-next-line no-console
      console.log('Adobe Analytics debugging: Tracked form event: ' + event, trackData, updatedDataLayer);
    };
  }
});
</script>
